// Profile management types and interfaces
import type { AdminActionDto, ProfileConnectionDto } from './api/appProfile';
import type { WellCheckDto, GrowthTrackDto, VaccineTrackDto, PrescriptionTrackDto, ReferralTrackDto } from '../storage/WellCheckStorage';

// Milestone state tracking for individual profiles
export interface MilestoneState {
  mvarid: string;
  state: 'yes' | 'sometimes' | 'not-yet';
  lastUpdated: string;
  notes?: string;
}

// Concern state tracking for individual profiles
export interface ConcernState {
  mvarid: string;
  state: 'yes' | 'no';
  lastUpdated: string;
  notes?: string;
  /** Follow-up action: 1=Track, 2=Manage, 3=Seek Help */
  action?: number;
}

// Enhanced child profile with comprehensive tracking
export interface ChildProfile {
  id: string;
  name: string;                // Display name (dname) — set from the "Profile Name" field on creation
  fname?: string;              // First name (PHI)
  lname?: string;              // Last name (PHI)
  email?: string;              // Optional email for profile identification
  accountnum: string;          // Unique account number
  dob: string;                 // Date of birth (ISO string)
  
  // Age tracking in months
  agephysical: number;         // Physical/chronological age in months
  
  // Development age tracking by domain
  devage: {
    communication: number | null;     // Development age for communication domain
    motor: number | null;            // Development age for motor domain (standard)
    finemotor: number | null;        // Development age for fine motor domain (premium)
    grossmotor: number | null;       // Development age for gross motor domain (premium)
    cognitive: number | null;        // Development age for cognitive domain
    socialemotional: number | null;  // Development age for social-emotional domain
  };
  
  // Floor age tracking (minimum demonstrated abilities) by domain
  devfloor: {
    communication: number | null;     // Lowest demonstrated communication abilities
    motor: number | null;            // Lowest demonstrated motor abilities (standard)
    finemotor: number | null;        // Lowest demonstrated fine motor abilities (premium)
    grossmotor: number | null;       // Lowest demonstrated gross motor abilities (premium)
    cognitive: number | null;        // Lowest demonstrated cognitive abilities
    socialemotional: number | null;  // Lowest demonstrated social-emotional abilities
  };
  
  // Ceiling age tracking (highest attempted abilities) by domain
  devceiling: {
    communication: number | null;     // Highest attempted communication abilities
    motor: number | null;            // Highest attempted motor abilities (standard)
    finemotor: number | null;        // Highest attempted fine motor abilities (premium)
    grossmotor: number | null;       // Highest attempted gross motor abilities (premium)
    cognitive: number | null;        // Highest attempted cognitive abilities
    socialemotional: number | null;  // Highest attempted social-emotional abilities
  };
  
  // Floor/ceiling calculation timestamps by domain
  devfloorCeilingTimestamp: {
    communication: string;     // Last floor/ceiling calculation for communication
    motor: string;            // Last floor/ceiling calculation for motor (standard)
    finemotor: string;        // Last floor/ceiling calculation for fine motor (premium)
    grossmotor: string;       // Last floor/ceiling calculation for gross motor (premium)
    cognitive: string;        // Last floor/ceiling calculation for cognitive
    socialemotional: string;  // Last floor/ceiling calculation for social-emotional
  };
  
  // Delay tracking (development delays) by domain
  devdelay: {
    communication: number | null;     // Communication development delay in months
    motor: number | null;            // Motor development delay in months (standard)
    finemotor: number | null;        // Fine motor development delay in months (premium)
    grossmotor: number | null;       // Gross motor development delay in months (premium)
    cognitive: number | null;        // Cognitive development delay in months
    socialemotional: number | null;  // Social-emotional development delay in months
  };
  
  // Result tracking (assessment results) by domain
  devresult: {
    communication: number | null;     // Communication assessment result score
    motor: number | null;            // Motor assessment result score (standard)
    finemotor: number | null;        // Fine motor assessment result score (premium)
    grossmotor: number | null;       // Gross motor assessment result score (premium)
    cognitive: number | null;        // Cognitive assessment result score
    socialemotional: number | null;  // Social-emotional assessment result score
  };
  
  // Result score tracking (computed result scores) by domain
  devresultscore: {
    communication: number | null;     // Communication computed result score
    motor: number | null;            // Motor computed result score (standard)
    finemotor: number | null;        // Fine motor computed result score (premium)
    grossmotor: number | null;       // Gross motor computed result score (premium)
    cognitive: number | null;        // Cognitive computed result score
    socialemotional: number | null;  // Social-emotional computed result score
  };

  // Timestamp tracking for when domain results were last computed (optional for backward compatibility)
  devresultcomputed?: {
    communication: string | null;     // ISO timestamp when communication results were computed
    motor: string | null;            // ISO timestamp when motor results were computed (standard)
    finemotor: string | null;        // ISO timestamp when fine motor results were computed (premium)
    grossmotor: string | null;       // ISO timestamp when gross motor results were computed (premium)
    cognitive: string | null;        // ISO timestamp when cognitive results were computed
    socialemotional: string | null;  // ISO timestamp when social-emotional results were computed
  };
  
  // Development state tracking by domain (0 = initial/uncomputed state)
  devstate: {
    communication: number;     // Communication development state (0 = initial)
    motor: number;            // Motor development state (0 = initial)
    finemotor: number;        // Fine motor development state (0 = initial)
    grossmotor: number;       // Gross motor development state (0 = initial)
    cognitive: number;        // Cognitive development state (0 = initial)
    socialemotional: number;  // Social-emotional development state (0 = initial)
  };
  
  gender: 'male' | 'female' | 'other' | 'prefer-not-to-say';
  
  // State tracking arrays
  milestonestate: MilestoneState[];  // Current milestone states
  concernsstate: ConcernState[];     // Current concern states
  actionsstate?: AdminActionDto[];   // Current action instances (AdminActionDto)
  wellchecks?: WellCheckDto[];       // Well-check visit state records
  growthtrack?: GrowthTrackDto;      // Growth measurement history
  vaccinetrack?: VaccineTrackDto;          // Vaccine administration history
  prescriptiontrack?: PrescriptionTrackDto; // Prescription history
  referraltrack?: ReferralTrackDto;         // Referral history
  connections?: ProfileConnectionDto[];     // Provider/agency/service connections
  
  // Profile metadata
  photo?: string;              // Base64 encoded or URL
  isPremium: boolean;          // Premium tier status
  createdDate: string;
  lastActiveDate: string;
  lastSyncDate?: string;       // Last sync with datacenter
  
  // Backend sync state management
  isDirty?: boolean;           // Profile needs to be synced to backend
  lastSyncAttempt?: string;    // Last attempted sync timestamp
  syncFailureReason?: string;  // Reason for last sync failure

  // Visibility / soft-delete
  isHidden?: boolean;          // True = profile is disabled; hidden from HomeView selector
  /** Backend profile status: 1 = active, 0 = disabled, -1 = deleted */
  profileStatus?: number;
}

// Legacy DevelopmentDomain interface (for backward compatibility)
export interface DevelopmentDomain {
  name: string;
  key: 'communication' | 'motor' | 'fineMotor' | 'grossMotor' | 'cognitive' | 'socialEmotional';
  ageMonths: number;
  percentile?: number;
  lastUpdated: string;
  icon?: string;
}

// Legacy DevelopmentAge interface (for backward compatibility)
export interface DevelopmentAge {
  profileId: string;
  domains: DevelopmentDomain[];
  overallAgeMonths: number;
  lastCalculated: string;
}

// Enhanced profile state with milestone and concern tracking
export interface ProfileState {
  profiles: ChildProfile[];
  activeProfileId: string | null;
  developmentAges: Record<string, DevelopmentAge>; // profileId -> DevelopmentAge (legacy)
  
  // Profile actions  
  addProfile: (profile: Partial<ChildProfile> & Pick<ChildProfile, 'name' | 'dob' | 'accountnum' | 'gender'>, options?: { skipBackendSync?: boolean }) => Promise<void>;
  updateProfile: (id: string, updates: Partial<ChildProfile>) => void;
  removeProfile: (id: string) => void;
  setActiveProfile: (id: string) => void;
  
  // Milestone state management
  updateMilestoneState: (profileId: string, mvarid: string, state: 'yes' | 'sometimes' | 'not-yet', notes?: string) => void;
  getMilestoneState: (profileId: string, mvarid: string) => MilestoneState | null;
  getMilestoneStates: (profileId: string) => MilestoneState[];
  
  // Concern state management
  updateConcernState: (profileId: string, mvarid: string, state: 'yes' | 'no', notes?: string) => void;
  getConcernState: (profileId: string, mvarid: string) => ConcernState | null;
  getConcernStates: (profileId: string) => ConcernState[];
  
  // Age management
  updateDevelopmentAges: (profileId: string, ages: Partial<{
    communication: number;
    motor: number;
    finemotor: number;
    grossmotor: number;
    cognitive: number;
    socialemotional: number;
  }>) => void;
  calculatePhysicalAge: (profileId: string) => number;
  
  // Development age actions (legacy support)
  updateDevelopmentAge: (profileId: string, domains: DevelopmentDomain[]) => void;
  calculateDevelopmentAge: (profileId: string) => void;
  
  // Utility getters
  getActiveProfile: () => ChildProfile | null;
  getActiveDevelopmentAge: () => DevelopmentAge | null;
  getProfileById: (id: string) => ChildProfile | null;
  
  // Statistics and insights
  getProfileStats: (profileId: string) => {
    totalMilestones: number;
    achievedMilestones: number;
    sometimesMilestones: number;
    notYetMilestones: number;
    totalConcerns: number;
    identifiedConcerns: number;
    developmentAgeGaps: {
      communication: number;
      motor: number;
      finemotor: number;
      grossmotor: number;
      cognitive: number;
      socialemotional: number;
    };
    floorAgeGaps: {
      communication: number;
      motor: number;
      finemotor: number;
      grossmotor: number;
      cognitive: number;
      socialemotional: number;
    };
    ceilingAgeGaps: {
      communication: number;
      motor: number;
      finemotor: number;
      grossmotor: number;
      cognitive: number;
      socialemotional: number;
    };
    delayTracking: {
      communication: number;
      motor: number;
      finemotor: number;
      grossmotor: number;
      cognitive: number;
      socialemotional: number;
    };
    assessmentResults: {
      communication: number;
      motor: number;
      finemotor: number;
      grossmotor: number;
      cognitive: number;
      socialemotional: number;
    };
    assessmentResultScores: {
      communication: number;
      motor: number;
      finemotor: number;
      grossmotor: number;
      cognitive: number;
      socialemotional: number;
    };
  };
  
  reset: () => void;
}

// Development age calculation utilities
export const calculateAgeInMonths = (dob: string): number => {
  // console.log('📋 calculateAgeInMonths: Input DOB string:', dob);
  const birth = new Date(dob);
  const now = new Date();
  
  // console.log('📋 calculateAgeInMonths: Birth date object:', birth);
  // console.log('📋 calculateAgeInMonths: Current date:', now);
  
  // Calculate precise difference in months with tenths
  const yearDiff = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  const dayDiff = now.getDate() - birth.getDate();
  
  // console.log('📋 calculateAgeInMonths: Year diff:', yearDiff, 'Month diff:', monthDiff, 'Day diff:', dayDiff);
  
  // Total months (including fractional part for days)
  let totalMonths = yearDiff * 12 + monthDiff;
  
  // Add fractional month based on days
  // Get the number of days in the current month for precise calculation
  const daysInCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const dayFraction = dayDiff / daysInCurrentMonth;
  totalMonths += dayFraction;
  
  // Round to one decimal place (tenths of a month)
  const result = Math.round(totalMonths * 10) / 10;
  // console.log('📋 calculateAgeInMonths: Final result:', result, 'months');
  return result;
};

// Create default milestone states for a new profile
export const createDefaultMilestoneStates = (): MilestoneState[] => {
  // Returns empty array - states will be populated as milestones are evaluated
  return [];
};

// Create default concern states for a new profile
export const createDefaultConcernStates = (): ConcernState[] => {
  // Returns empty array - states will be populated as concerns are evaluated
  return [];
};

// Create a new child profile with default values
export const createChildProfile = (
  profileData: Omit<ChildProfile, 'id' | 'milestonestate' | 'concernsstate' | 'createdDate' | 'lastActiveDate' | 'agephysical' | 'devage' | 'devfloor' | 'devceiling' | 'devdelay' | 'devresult' | 'devresultscore' | 'devresultcomputed' | 'devstate'>
): Omit<ChildProfile, 'id'> => {
  console.log('📋 createChildProfile: Input DOB:', profileData.dob);
  const physicalAge = calculateAgeInMonths(profileData.dob);
  console.log('📋 createChildProfile: Calculated physical age:', physicalAge, 'months');
  
  return {
    ...profileData,
    agephysical: physicalAge,
    
    // Initialize developmental ages to null (no assessments completed yet)
    devage: createDefaultDomainValues(null),
    
    // Initialize floor ages to null (no demonstrated abilities yet)
    devfloor: createDefaultDomainValues(null),
    
    // Initialize ceiling ages to null (no attempted abilities yet)
    devceiling: createDefaultDomainValues(null),
    
    // Initialize delay tracking to null (no delays calculated yet)
    devdelay: createDefaultDomainValues(null),
    
    // Initialize result tracking to null (no assessments completed yet)
    devresult: createDefaultDomainValues(null),
    
    // Initialize result score tracking to null (no result scores calculated yet)
    devresultscore: createDefaultDomainValues(null),
    
    // Initialize result computation timestamps to null (no results computed yet)
    devresultcomputed: createDefaultDomainStrings(null),
    
    // Initialize development state tracking to 0 (initial/uncomputed state)
    devstate: createDefaultDomainNumbers(0),
    
    milestonestate: createDefaultMilestoneStates(),
    concernsstate: createDefaultConcernStates(),
    createdDate: new Date().toISOString(),
    lastActiveDate: new Date().toISOString(),
  };
};

// Calculate development age gaps
export const calculateDevelopmentGaps = (profile: ChildProfile) => {
  const physical = profile.agephysical;
  
  return {
    // Development age gaps
    communication: (profile.devage.communication ?? 0) - physical,
    motor: (profile.devage.motor ?? 0) - physical,
    finemotor: (profile.devage.finemotor ?? 0) - physical,
    grossmotor: (profile.devage.grossmotor ?? 0) - physical,
    cognitive: (profile.devage.cognitive ?? 0) - physical,
    socialemotional: (profile.devage.socialemotional ?? 0) - physical,
    
    // Floor age gaps (demonstrated abilities vs physical age)
    floorcommunication: (profile.devfloor.communication ?? 0) - physical,
    floormotor: (profile.devfloor.motor ?? 0) - physical,
    floorfinemotor: (profile.devfloor.finemotor ?? 0) - physical,
    floorgrossmotor: (profile.devfloor.grossmotor ?? 0) - physical,
    floorcognitive: (profile.devfloor.cognitive ?? 0) - physical,
    floorsocialemotional: (profile.devfloor.socialemotional ?? 0) - physical,
    
    // Ceiling age gaps (attempted abilities vs physical age)
    ceilingcommunication: (profile.devceiling.communication ?? 0) - physical,
    ceilingmotor: (profile.devceiling.motor ?? 0) - physical,
    ceilingfinemotor: (profile.devceiling.finemotor ?? 0) - physical,
    ceilinggrossmotor: (profile.devceiling.grossmotor ?? 0) - physical,
    ceilingcognitive: (profile.devceiling.cognitive ?? 0) - physical,
    ceilingsocialemotional: (profile.devceiling.socialemotional ?? 0) - physical,
    
    // Age ranges (ceiling - floor for each domain)
    rangecommunication: (profile.devceiling.communication ?? 0) - (profile.devfloor.communication ?? 0),
    rangemotor: (profile.devceiling.motor ?? 0) - (profile.devfloor.motor ?? 0),
    rangefinemotor: (profile.devceiling.finemotor ?? 0) - (profile.devfloor.finemotor ?? 0),
    rangegrossmotor: (profile.devceiling.grossmotor ?? 0) - (profile.devfloor.grossmotor ?? 0),
    rangecognitive: (profile.devceiling.cognitive ?? 0) - (profile.devfloor.cognitive ?? 0),
    rangesocialemotional: (profile.devceiling.socialemotional ?? 0) - (profile.devfloor.socialemotional ?? 0),
    
    // Delay gaps (development delays vs expected)
    delaycommunication: profile.devdelay.communication ?? 0,
    delaymotor: profile.devdelay.motor ?? 0,
    delayfinemotor: profile.devdelay.finemotor ?? 0,
    delaygrossmotor: profile.devdelay.grossmotor ?? 0,
    delaycognitive: profile.devdelay.cognitive ?? 0,
    delaysocialemotional: profile.devdelay.socialemotional ?? 0,
    
    // Result scores by domain
    resultcommunication: profile.devresult.communication ?? 0,
    resultmotor: profile.devresult.motor ?? 0,
    resultfinemotor: profile.devresult.finemotor ?? 0,
    resultgrossmotor: profile.devresult.grossmotor ?? 0,
    resultcognitive: profile.devresult.cognitive ?? 0,
    resultsocialemotional: profile.devresult.socialemotional ?? 0,
  };
};

// Get development domains based on tier (legacy support)
export const getStandardDomains = (): DevelopmentDomain[] => [
  {
    name: 'Communication',
    key: 'communication',
    ageMonths: 0,
    lastUpdated: new Date().toISOString(),
    icon: '💬',
  },
  {
    name: 'Motor Skills',
    key: 'motor',
    ageMonths: 0,
    lastUpdated: new Date().toISOString(),
    icon: '🏃',
  },
  {
    name: 'Cognitive',
    key: 'cognitive',
    ageMonths: 0,
    lastUpdated: new Date().toISOString(),
    icon: '🧩',
  },
  {
    name: 'Social-Emotional',
    key: 'socialEmotional',
    ageMonths: 0,
    lastUpdated: new Date().toISOString(),
    icon: '❤️',
  },
];

export const getPremiumDomains = (): DevelopmentDomain[] => [
  {
    name: 'Communication',
    key: 'communication',
    ageMonths: 0,
    lastUpdated: new Date().toISOString(),
    icon: '💬',
  },
  {
    name: 'Gross Motor',
    key: 'grossMotor',
    ageMonths: 0,
    lastUpdated: new Date().toISOString(),
    icon: '🏃',
  },
  {
    name: 'Fine Motor',
    key: 'fineMotor',
    ageMonths: 0,
    lastUpdated: new Date().toISOString(),
    icon: '✍️',
  },
  {
    name: 'Cognitive',
    key: 'cognitive',
    ageMonths: 0,
    lastUpdated: new Date().toISOString(),
    icon: '🧩',
  },
  {
    name: 'Social-Emotional',
    key: 'socialEmotional',
    ageMonths: 0,
    lastUpdated: new Date().toISOString(),
    icon: '❤️',
  },
];

// Milestone state utilities
export const updateMilestoneState = (
  states: MilestoneState[], 
  mvarid: string, 
  state: 'yes' | 'sometimes' | 'not-yet',
  notes?: string
): MilestoneState[] => {
  const existingIndex = states.findIndex(s => s.mvarid === mvarid);
  const newState: MilestoneState = {
    mvarid,
    state,
    lastUpdated: new Date().toISOString(),
    notes,
  };

  if (existingIndex >= 0) {
    const updated = [...states];
    updated[existingIndex] = newState;
    return updated;
  } else {
    return [...states, newState];
  }
};

// Concern state utilities
export const updateConcernState = (
  states: ConcernState[], 
  mvarid: string, 
  state: 'yes' | 'no',
  notes?: string
): ConcernState[] => {
  const existingIndex = states.findIndex(s => s.mvarid === mvarid);
  const newState: ConcernState = {
    mvarid,
    state,
    lastUpdated: new Date().toISOString(),
    notes,
  };

  if (existingIndex >= 0) {
    const updated = [...states];
    updated[existingIndex] = newState;
    return updated;
  } else {
    return [...states, newState];
  }
};

// Profile validation utilities
export const validateChildProfile = (profile: Partial<ChildProfile>): string[] => {
  const errors: string[] = [];
  
  if (!profile.name?.trim()) errors.push('Name is required');
  if (!profile.accountnum?.trim()) errors.push('Account number is required');
  if (!profile.dob) errors.push('Date of birth is required');
  if (profile.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) {
    errors.push('Invalid email format');
  }
  
  // Age validations
  if (typeof profile.agephysical === 'number' && (profile.agephysical < 0 || profile.agephysical > 300)) {
    errors.push('Physical age must be between 0-300 months');
  }
  
  // Gender validation
  if (profile.gender && !['male', 'female', 'other', 'prefer-not-to-say'].includes(profile.gender)) {
    errors.push('Invalid gender value');
  }
  
  return errors;
};

export const formatAgeDisplay = (ageMonths: number): string => {
  if (ageMonths < 12) {
    return `${ageMonths} month${ageMonths !== 1 ? 's' : ''}`;
  }
  
  const years = Math.floor(ageMonths / 12);
  const months = ageMonths % 12;
  
  if (months === 0) {
    return `${years} year${years !== 1 ? 's' : ''}`;
  }
  
  return `${years}y ${months}m`;
};

// Helper functions for working with domain-based age tracking
export type DomainKey = 'communication' | 'motor' | 'finemotor' | 'grossmotor' | 'cognitive' | 'socialemotional';

export const getDomainKeys = (): DomainKey[] => [
  'communication', 'motor', 'finemotor', 'grossmotor', 'cognitive', 'socialemotional'
];

export const getStandardDomainKeys = (): DomainKey[] => [
  'communication', 'motor', 'cognitive', 'socialemotional'
];

export const getPremiumDomainKeys = (): DomainKey[] => [
  'communication', 'finemotor', 'grossmotor', 'cognitive', 'socialemotional'
];

// Helper to update specific domain values
export const updateDomainValue = <T extends Record<DomainKey, number | null>>(
  current: T,
  domain: DomainKey,
  value: number | null
): T => ({
  ...current,
  [domain]: value,
});

// Helper to get domain value safely
export const getDomainValue = <T extends Record<DomainKey, number | null>>(
  data: T,
  domain: DomainKey
): number => data[domain] ?? 0;

// Helper to initialize default domain values
export const createDefaultDomainValues = (defaultValue: number | null = 0): Record<DomainKey, number | null> => ({
  communication: defaultValue,
  motor: defaultValue,
  finemotor: defaultValue,
  grossmotor: defaultValue,
  cognitive: defaultValue,
  socialemotional: defaultValue,
});

// Helper to initialize default domain values (non-nullable)
export const createDefaultDomainNumbers = (defaultValue: number = 0): Record<DomainKey, number> => ({
  communication: defaultValue,
  motor: defaultValue,
  finemotor: defaultValue,
  grossmotor: defaultValue,
  cognitive: defaultValue,
  socialemotional: defaultValue,
});

// Helper to initialize default domain string values (for timestamps)
export const createDefaultDomainStrings = (defaultValue: string | null = null): Record<DomainKey, string | null> => ({
  communication: defaultValue,
  motor: defaultValue,
  finemotor: defaultValue,
  grossmotor: defaultValue,
  cognitive: defaultValue,
  socialemotional: defaultValue,
});

// Migration helper to ensure profiles have all required fields
export const migrateProfileData = (profile: any): ChildProfile => {
  // Ensure devresultcomputed exists for backward compatibility
  if (!profile.devresultcomputed) {
    profile.devresultcomputed = createDefaultDomainStrings(null);
  }
  
  // Ensure all required fields exist with defaults
  return {
    id: profile.id || '',
    name: profile.name || 'Unknown',
    email: profile.email,
    accountnum: profile.accountnum || '',
    dob: profile.dob || new Date().toISOString(),
    agephysical: profile.agephysical || 0,
    devage: profile.devage || createDefaultDomainValues(null),
    devfloor: profile.devfloor || createDefaultDomainValues(null),
    devceiling: profile.devceiling || createDefaultDomainValues(null),
    devfloorCeilingTimestamp: profile.devfloorCeilingTimestamp || {
      communication: new Date().toISOString(),
      motor: new Date().toISOString(),
      finemotor: new Date().toISOString(),
      grossmotor: new Date().toISOString(),
      cognitive: new Date().toISOString(),
      socialemotional: new Date().toISOString(),
    },
    devdelay: profile.devdelay || createDefaultDomainValues(null),
    devresult: profile.devresult || createDefaultDomainValues(null),
    devresultscore: profile.devresultscore || createDefaultDomainValues(null),
    devresultcomputed: profile.devresultcomputed || createDefaultDomainStrings(null),
    devstate: profile.devstate || createDefaultDomainNumbers(0),
    gender: profile.gender || 'prefer-not-to-say',
    milestonestate: profile.milestonestate || [],
    concernsstate: profile.concernsstate || [],
    actionsstate: profile.actionsstate || [],
    wellchecks: profile.wellchecks || [],
    growthtrack: profile.growthtrack || { data: [] },
    vaccinetrack: profile.vaccinetrack || { events: [] },
    isPremium: profile.isPremium || false,
    createdDate: profile.createdDate || new Date().toISOString(),
    lastActiveDate: profile.lastActiveDate || new Date().toISOString(),
    lastSyncDate: profile.lastSyncDate,
    isDirty: profile.isDirty || false
  } as ChildProfile;
};

